import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faChevronDown,
  faHouse,
  faRightFromBracket,
  faArrowLeft,
  faUserShield,
  faArrowRight,
  faTools,
  faClipboardList,
  faComments,
  faBars,
  faTimes,
  faDatabase,
  faFileCsv,
  faEnvelopeOpenText,
  faBarChart,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import BASE_URL from "../config";
import "./Sidebar.css";

// ─────────────────────────────────────────────
// CONSTANTS — nav config, single source of truth
// ─────────────────────────────────────────────

const ADMIN_LINKS = [
  { to: "/teams",               label: "Teams & Projects"    },
  { to: "/information",         label: "Info"                },
  { to: "/clients-information", label: "Clients Information" },
  { to: "/pages",               label: "Pages"               },
  { to: "/users",               label: "Users"               },
  { to: "/clients",             label: "Clients"             },
  { to: "/admin-reoccurring",   label: "Reoccurring Tasks"   },
  { to: "/create-team",         label: "Create Team"         },
];

// Core task management — My Tasks, Bulk Tasks, Base44 Sups
const TASK_LINKS = [
  { to: "/my-tasks",       label: "My Tasks"    },
  { to: "/bulk-task-adder", label: "Bulk Tasks" },
];

// Reports — Ongage, API Stats (moved out of Tasks)
const REPORT_LINKS = [
  { to: "/ongage-export", label: "Ongage"    },
  { to: "/api-stats",     label: "API Stats" },
];

// Email Tools — Email Lead Extractor (moved out of Tasks)
const EMAIL_TOOL_LINKS = [
  { to: "/emailsorting",         label: "Email Sorting"        },  // Rizwan — was in More
  { to: "/emailer",              label: "Emailer"              },  // Rizwan — was in More
  { to: "/delete-email",         label: "Delete Email"         },  // Rizwan — was in More
  { to: "/search-email",         label: "Search Email"         },  // Rizwan — was in More
];

// ESP — client sending configs (removed Warmup Clean & Merge CSVs — moved to CSV Tools)
const ESP_LINKS = [
  { to: "/firstpremierlending", label: "First Premier Lending" },
  { to: "/lizbuyshomes",        label: "Liz Buys Homes"        },
  { to: "/bestrate",            label: "Best Rate"             },
  { to: "/warmup",              label: "Warmup"                },
  { to: "/domaincount",         label: "Domain Count"          },
  { to: "/template-setter",     label: "Template Setter"       },
];

// Analytics — standalone parent (moved out of ESP)
const ANALYTICS_LINKS = [
  { to: "/analytics", label: "Analytics" },
];

// CSV Tools — Rizwan only (merged from ESP + More)
const CSV_TOOL_LINKS = [
  { to: "/mergecsv",    label: "Merge CSVs"  },   // was in ESP
  { to: "/warmupclean", label: "Warmup Clean" },   // was in ESP
  { to: "/merger-csv",  label: "Merger CSV"   },   // was in More
];

// Database — Rizwan only (moved out of More + Tasks)
const DATABASE_LINKS = [
  { to: "/database-shrinker",   label: "Database Shrinker"   },  // was in Tasks
  { to: "/delete-email",        label: "Delete Email"         },  // shared with Email Tools
  { to: "/search-email",        label: "Search Email"         },  // shared with Email Tools
  { to: "/database-transaction", label: "Transactions"        },  // was in More
  { to: "/master-suppressions", label: "Master Suppressions"  },  // was in More
];

// Utilities — Rizwan only (moved out of More)
const UTILITY_LINKS = [
  { to: "/suppressioncleaninglog", label: "Suppression Cleaning Log" },
  { to: "/importwithprogress",     label: "Import With Progress"     },
  { to: "/optizmo",                label: "Optizmo"                  },
  { to: "/domains",                label: "Domains"                  },
  { to: "/keeper",                 label: "Keeper File"              },
];

const RIZWAN_EMAIL = "rizwan@softisans.com";

// ─────────────────────────────────────────────
// HOOK — encapsulates all sidebar business logic
// ─────────────────────────────────────────────

const useSidebar = (onToggle) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [email, setEmail] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchEmail = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${BASE_URL}api/user/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setEmail(data.email);
        } else {
          console.error("Failed to fetch user profile.");
        }
      } catch (error) {
        console.error("Error fetching email:", error);
      }
    };

    fetchEmail();
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((prev) => {
      onToggle(!prev);
      return !prev;
    });
  }, [onToggle]);

  const toggleMobileSidebar = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const handleNavigation = useCallback(
    (e, path) => {
      if (location.pathname === path) {
        e.preventDefault();
      }
      closeMobileSidebar();
    },
    [location.pathname, closeMobileSidebar]
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  }, []);

  const isRizwan = email === RIZWAN_EMAIL;

  return {
    isCollapsed,
    isMobileOpen,
    isRizwan,
    toggleSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    handleNavigation,
    handleLogout,
  };
};

// ─────────────────────────────────────────────
// PRESENTATIONAL — NavLink (single menu item)
// ─────────────────────────────────────────────

const NavLink = ({ to, label, onNavigate }) => (
  <li>
    <Link to={to} onClick={(e) => onNavigate(e, to)}>
      <span className="menu-text">{label}</span>
    </Link>
  </li>
);

// ─────────────────────────────────────────────
// PRESENTATIONAL — DropdownMenu (collapsible section)
// ─────────────────────────────────────────────

const DropdownMenu = ({ icon, label, targetId, children }) => (
  <>
    <li className="dropdown">
      <a href="#" data-bs-toggle="collapse" data-bs-target={`#${targetId}`} aria-expanded="false">
        <FontAwesomeIcon icon={icon} style={{ color: "white" }} />
        <span className="menu-text">{label}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="collapse-icon"
          style={{ color: "white", fontSize: "0.8em" }}
        />
      </a>
    </li>
    <ul id={targetId} className="collapse submenu">
      {children}
    </ul>
  </>
);

// ─────────────────────────────────────────────
// PRESENTATIONAL — SidebarLogo
// ─────────────────────────────────────────────

const SidebarLogo = ({ onClose }) => (
  <div className="logo">
    <Link to="/" onClick={onClose}>
      <img
        src="https://res.cloudinary.com/softisans-com/image/upload/v1592236220/Logo/Softisans-Logo.png"
        alt="Softisans Logo"
      />
    </Link>
  </div>
);

// ─────────────────────────────────────────────
// PRESENTATIONAL — SidebarToggleButton (desktop)
// ─────────────────────────────────────────────

const SidebarToggleButton = ({ isCollapsed, onToggle }) => (
  <div className="toggle-btn desktop-only" onClick={onToggle}>
    <FontAwesomeIcon
      icon={isCollapsed ? faArrowRight : faArrowLeft}
      style={{ color: "#39c09a" }}
    />
  </div>
);

// ─────────────────────────────────────────────
// PRESENTATIONAL — MobileMenuButton
// ─────────────────────────────────────────────

const MobileMenuButton = ({ isOpen, onToggle }) => (
  <button className="mobile-menu-btn" onClick={onToggle}>
    <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
  </button>
);

// ─────────────────────────────────────────────
// PRESENTATIONAL — MobileOverlay
// ─────────────────────────────────────────────

const MobileOverlay = ({ isVisible, onClick }) =>
  isVisible ? <div className="mobile-overlay" onClick={onClick} /> : null;

// ─────────────────────────────────────────────
// PRESENTATIONAL — SidebarNav (full nav tree)
// ─────────────────────────────────────────────

const SidebarNav = ({ isRizwan, handleNavigation, handleLogout }) => (
  <ul>
    {/* ── Dashboard ───────────────────────── */}
    <li>
      <Link to="/dashboard" onClick={(e) => handleNavigation(e, "/dashboard")}>
        <FontAwesomeIcon icon={faHouse} style={{ color: "white" }} />
        <span className="menu-text">Dashboard</span>
      </Link>
    </li>

    {/* ── Administration ──────────────────── */}
    <DropdownMenu icon={faUserShield} label="Administration" targetId="adminMenu">
      {ADMIN_LINKS.map(({ to, label }) => (
        <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
      ))}
    </DropdownMenu>

    {/* ── Tasks ───────────────────────────── */}
    <DropdownMenu icon={faClipboardList} label="Tasks" targetId="tasksMenu">
      {TASK_LINKS.map(({ to, label }) => (
        <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
      ))}
    </DropdownMenu>

    {/* ── Reports (new) ───────────────────── */}
    <DropdownMenu icon={faBarChart} label="Reports" targetId="reportsMenu">
      {REPORT_LINKS.map(({ to, label }) => (
        <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
      ))}
    </DropdownMenu>

    {/* ── Email Tools (new) ───────────────── */}
    <DropdownMenu icon={faEnvelopeOpenText} label="Email Tools" targetId="emailToolsMenu">
      {EMAIL_TOOL_LINKS.map(({ to, label }) => (
        <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
      ))}
    </DropdownMenu>

    {/* ── ESP ─────────────────────────────── */}
    <DropdownMenu icon={faChartLine} label="ESP" targetId="relayzoMenu">
      {ESP_LINKS.map(({ to, label }) => (
        <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
      ))}
    </DropdownMenu>

    {/* ── Analytics (new standalone) ──────── */}
    <DropdownMenu icon={faBarChart} label="Analytics" targetId="analyticsMenu">
      {ANALYTICS_LINKS.map(({ to, label }) => (
        <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
      ))}
    </DropdownMenu>

    {/* ── Rizwan-only parents ──────────────── */}
    {isRizwan && (
      <>
        {/* CSV Tools */}
        <DropdownMenu icon={faFileCsv} label="CSV Tools" targetId="csvToolsMenu">
          {CSV_TOOL_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
          ))}
        </DropdownMenu>

        {/* Database */}
        <DropdownMenu icon={faDatabase} label="Database" targetId="databaseMenu">
          {DATABASE_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
          ))}
        </DropdownMenu>

        {/* Utilities */}
        <DropdownMenu icon={faWrench} label="Utilities" targetId="utilitiesMenu">
          {UTILITY_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} label={label} onNavigate={handleNavigation} />
          ))}
        </DropdownMenu>
      </>
    )}

    {/* ── Chat ────────────────────────────── */}
    <li>
      <a href="https://chat.google.com/" target="_blank" rel="noopener noreferrer">
        <FontAwesomeIcon icon={faComments} style={{ color: "white" }} />
        <span className="menu-text">Chat</span>
      </a>
    </li>

    {/* ── Logout ──────────────────────────── */}
    <li>
      <Link to="#" onClick={handleLogout}>
        <FontAwesomeIcon icon={faRightFromBracket} style={{ color: "white" }} />
        <span className="menu-text">Logout</span>
      </Link>
    </li>
  </ul>
);

// ─────────────────────────────────────────────
// CONTAINER — Sidebar (orchestrates everything)
// ─────────────────────────────────────────────

const Sidebar = ({ onToggle = () => {} }) => {
  const {
    isCollapsed,
    isMobileOpen,
    isRizwan,
    toggleSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    handleNavigation,
    handleLogout,
  } = useSidebar(onToggle);

  return (
    <>
      <MobileMenuButton isOpen={isMobileOpen} onToggle={toggleMobileSidebar} />
      <MobileOverlay isVisible={isMobileOpen} onClick={toggleMobileSidebar} />

      <div
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isMobileOpen ? "mobile-open" : ""}`}
      >
        <SidebarLogo onClose={closeMobileSidebar} />
        <SidebarToggleButton isCollapsed={isCollapsed} onToggle={toggleSidebar} />
        <SidebarNav
          isRizwan={isRizwan}
          handleNavigation={handleNavigation}
          handleLogout={handleLogout}
        />
      </div>
    </>
  );
};

export default Sidebar;